/**
 * Reporting (admin only) — vendor-subscription profit.
 *
 * Fetches the profit baseline once, then derives three views client-side:
 * profit by product, profit by customer, and a what-if simulator that overrides
 * per-SKU buy/sell prices and recomputes live. All figures are GST-exclusive and
 * normalized to Monthly Recurring Revenue (MRR). See profitCompute.ts for the
 * shared math and docs/architecture/subscriptions-reconciliation-module.md.
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  Alert,
  CircularProgress,
  FormControlLabel,
  Switch,
  Stack,
  Snackbar,
} from '@mui/material';
import type { ProfitBaselineRow, ProfitProjection } from '@techgitter/contracts';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { fetchProfitBaseline } from '../services/reportingApi';
import {
  fetchProjections,
  createProjection,
  updateProjection,
  deleteProjection,
} from '../services/projectionsApi';
import { applyOverrides, type OverrideMap, type SkuOverride } from '../utils/profitCompute';
import { ProfitByProductTable } from '../components/reporting/ProfitByProductTable';
import { ProfitByCustomerTable } from '../components/reporting/ProfitByCustomerTable';
import { WhatIfSimulatorPanel } from '../components/reporting/WhatIfSimulatorPanel';
import { ProjectionsBar } from '../components/reporting/ProjectionsBar';

type TabKey = 'product' | 'customer' | 'simulator';

const hasUnknownMargin = (r: ProfitBaselineRow): boolean =>
  r.mrrProfit === null || r.flags.includes('unknownBuy') || r.flags.includes('unknownSell');

/** Stable JSON (sorted keys) so override key order doesn't trigger false "dirty". */
const stableStringify = (obj: unknown): string =>
  JSON.stringify(obj, (_k, v) =>
    v && typeof v === 'object' && !Array.isArray(v)
      ? Object.keys(v).sort().reduce((o, k) => ({ ...o, [k]: (v as Record<string, unknown>)[k] }), {})
      : v,
  );

export const ReportingPage: React.FC = () => {
  const currentUser = useCurrentUser();
  const isAdmin = !!currentUser?.hasAnyRole('Admin');

  const [rows, setRows] = useState<ProfitBaselineRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [tab, setTab] = useState<TabKey>('product');
  const [overrides, setOverrides] = useState<OverrideMap>({});
  // Per-SKU % increase applied to buy/sell. The economics live in `overrides`;
  // this is the UI annotation, saved alongside so the "% incr." column
  // repopulates when a projection is recalled.
  const [pctBySku, setPctBySku] = useState<Record<string, number>>({});
  const [hideUnknown, setHideUnknown] = useState(false);

  const [projections, setProjections] = useState<ProfitProjection[]>([]);
  const [activeProjectionId, setActiveProjectionId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) return;
    const controller = new AbortController();
    setLoading(true);
    fetchProfitBaseline(controller.signal)
      .then(items => {
        if (!controller.signal.aborted) {
          setRows(items);
          setError(null);
        }
      })
      .catch(err => {
        if (!controller.signal.aborted) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [isAdmin]);

  useEffect(() => {
    if (!isAdmin) return;
    const controller = new AbortController();
    fetchProjections(controller.signal)
      .then(items => {
        if (!controller.signal.aborted) setProjections(items);
      })
      .catch(() => {
        /* projections are optional — table may not exist yet */
      });
    return () => controller.abort();
  }, [isAdmin]);

  const activeProjection = projections.find(p => p.id === activeProjectionId) ?? null;
  const dirty =
    stableStringify(overrides) !== stableStringify(activeProjection?.overrides ?? {}) ||
    stableStringify(pctBySku) !== stableStringify(activeProjection?.pctBySku ?? {});

  const refreshProjections = async (): Promise<ProfitProjection[]> => {
    const items = await fetchProjections();
    setProjections(items);
    return items;
  };

  const runProjectionOp = async (op: () => Promise<void>, ok: string) => {
    setBusy(true);
    try {
      await op();
      setToast(ok);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleLoadProjection = (id: string) => {
    const p = projections.find(x => x.id === id);
    if (!p) return;
    setOverrides(p.overrides as OverrideMap);
    setPctBySku(p.pctBySku ?? {});
    setActiveProjectionId(p.id);
  };

  const handleNewProjection = () => {
    setOverrides({});
    setPctBySku({});
    setActiveProjectionId(null);
  };

  const handleResetOverrides = () => {
    setOverrides({});
    setPctBySku({});
  };

  const handlePctChange = (sku: string, pct: number | null) =>
    setPctBySku(prev => {
      const next = { ...prev };
      if (pct === null) delete next[sku];
      else next[sku] = pct;
      return next;
    });

  const handleSaveProjection = () =>
    activeProjectionId &&
    runProjectionOp(async () => {
      await updateProjection(activeProjectionId, { overrides, pctBySku });
      await refreshProjections();
    }, 'Projection saved');

  const handleSaveAsProjection = (name: string) =>
    runProjectionOp(async () => {
      const created = await createProjection({ name, overrides, pctBySku });
      await refreshProjections();
      setActiveProjectionId(created.id);
    }, 'Projection saved');

  const handleRenameProjection = (name: string) =>
    activeProjectionId &&
    runProjectionOp(async () => {
      await updateProjection(activeProjectionId, { name });
      await refreshProjections();
    }, 'Projection renamed');

  const handleDeleteProjection = () =>
    activeProjectionId &&
    runProjectionOp(async () => {
      await deleteProjection(activeProjectionId);
      await refreshProjections();
      setActiveProjectionId(null);
      setOverrides({});
      setPctBySku({});
    }, 'Projection deleted');

  const visibleRows = useMemo(
    () => (hideUnknown ? rows.filter(r => !hasUnknownMargin(r)) : rows),
    [rows, hideUnknown],
  );
  const computedRows = useMemo(() => applyOverrides(visibleRows, overrides), [visibleRows, overrides]);

  const handleOverride = (sku: string, override: SkuOverride) => {
    setOverrides(prev => {
      const next = { ...prev };
      // Drop the entry entirely when every field is cleared.
      const empty =
        override.buyM2mExTax == null &&
        override.buyAnnualExTax == null &&
        override.sellM2mExTax == null &&
        override.sellAnnualExTax == null;
      if (empty) {
        delete next[sku];
      } else {
        next[sku] = override;
      }
      return next;
    });
  };

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Reporting is available to administrators only.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h5">Reporting — Subscription Profit</Typography>
        <Typography variant="caption" color="text.secondary">
          All figures GST-exclusive, monthly recurring (MRR)
        </Typography>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pr: 2 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)}>
            <Tab value="product" label="Profit by Product" />
            <Tab value="customer" label="Profit by Customer" />
            <Tab value="simulator" label="What-If Simulator" />
          </Tabs>
          {tab !== 'simulator' && (
            <FormControlLabel
              control={<Switch size="small" checked={hideUnknown} onChange={e => setHideUnknown(e.target.checked)} />}
              label={<Typography variant="caption">Hide unknown-margin rows</Typography>}
            />
          )}
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {tab === 'product' && <ProfitByProductTable rows={computedRows} />}
          {tab === 'customer' && <ProfitByCustomerTable rows={computedRows} />}
          {tab === 'simulator' && (
            <>
              <ProjectionsBar
                projections={projections}
                activeId={activeProjectionId}
                dirty={dirty}
                busy={busy}
                onLoad={handleLoadProjection}
                onNew={handleNewProjection}
                onSave={handleSaveProjection}
                onSaveAs={handleSaveAsProjection}
                onRename={handleRenameProjection}
                onDelete={handleDeleteProjection}
              />
              <WhatIfSimulatorPanel
                rows={visibleRows}
                overrides={overrides}
                pctBySku={pctBySku}
                onChange={handleOverride}
                onPctChange={handlePctChange}
                onReset={handleResetOverrides}
              />
            </>
          )}
        </>
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        message={toast ?? ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};
